
import { useState, useEffect } from 'react';

interface CartItem {
  id: number;
  productId: number;
  name: string;
  price: number;
  quantity: number;
  size: string;
  image: string;
}

const CART_STORAGE_KEY = 'luxe_cart';

export function useCart() {
  const [cartItems, setCartItems] = useState<CartItem[]>([]);

  useEffect(() => {
    const stored = localStorage.getItem(CART_STORAGE_KEY);
    if (stored) {
      try {
        setCartItems(JSON.parse(stored));
      } catch (e) {
        console.error('Failed to parse cart data', e);
      }
    }
  }, []);

  const saveCart = (items: CartItem[]) => {
    setCartItems(items);
    localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(items));
  };

  const addToCart = (product: {
    id: number;
    name: string;
    price: number;
    image: string;
    size: string;
    quantity: number;
  }) => {
    const existingItem = cartItems.find(
      (item) => item.productId === product.id && item.size === product.size
    );

    if (existingItem) {
      const updatedItems = cartItems.map((item) =>
        item.id === existingItem.id
          ? { ...item, quantity: item.quantity + product.quantity }
          : item
      );
      saveCart(updatedItems);
    } else {
      const newItem: CartItem = {
        id: Date.now(),
        productId: product.id,
        name: product.name,
        price: product.price,
        quantity: product.quantity,
        size: product.size,
        image: product.image,
      };
      saveCart([...cartItems, newItem]);
    }
  };

  const updateQuantity = (itemId: number, quantity: number) => {
    if (quantity <= 0) {
      removeFromCart(itemId);
      return;
    }
    const updatedItems = cartItems.map((item) =>
      item.id === itemId ? { ...item, quantity } : item
    );
    saveCart(updatedItems);
  };

  const removeFromCart = (itemId: number) => {
    const updatedItems = cartItems.filter((item) => item.id !== itemId);
    saveCart(updatedItems);
  };

  const clearCart = () => {
    saveCart([]);
  };

  const getCartTotal = () => {
    return cartItems.reduce((total, item) => total + item.price * item.quantity, 0);
  };

  const getCartCount = () => {
    return cartItems.reduce((count, item) => count + item.quantity, 0);
  };

  return {
    cartItems,
    addToCart,
    updateQuantity,
    removeFromCart,
    clearCart,
    getCartTotal,
    getCartCount,
  };
}
